import { CSRF_COOKIE_NAME } from '@chukua/common';

/**
 * Thin fetch wrapper. The web app is served from the same origin as the API
 * (e.g. index.chukua.app/api), so session cookies ride along without CORS.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`API ${status}`);
    this.name = 'ApiError';
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Called on any 401 — the thrown ApiError still propagates too. Returns an unsubscribe function. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/** Echoes the JS-readable CSRF cookie back as a header — CsrfGuard compares the two. Safe methods are exempt, same as the API. */
function csrfHeaders(method: string): Record<string, string> {
  if (SAFE_METHODS.has(method)) return {};
  const csrfToken = readCookie(CSRF_COOKIE_NAME);
  return csrfToken ? { 'x-csrf-token': csrfToken } : {};
}

export interface Download {
  blob: Blob;
  headers: Headers;
}

/** A GET whose answer is a file. A refusal still carries a JSON body (a 422 explains itself), which becomes the `ApiError`'s, as in `api()`. */
export async function apiDownload(path: string): Promise<Download> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401) unauthorizedListeners.forEach((listener) => listener());
    throw new ApiError(res.status, await res.json().catch(() => undefined));
  }
  return { blob: await res.blob(), headers: res.headers };
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  // FormData sets its own multipart boundary in the content-type header; let fetch handle it.
  const isFormData = init.body instanceof FormData;
  const res = await fetch(`/api/v1${path}`, {
    ...init,
    method,
    credentials: 'include',
    headers: { ...(isFormData ? {} : { 'content-type': 'application/json' }), ...csrfHeaders(method), ...init.headers },
  });
  const body = res.status === 204 ? undefined : await res.json().catch(() => undefined);
  if (!res.ok) {
    if (res.status === 401) unauthorizedListeners.forEach((listener) => listener());
    throw new ApiError(res.status, body);
  }
  return body as T;
}
